'use client';

import { useState, useCallback } from 'react';

interface UseAccordionOptions {
  totalSections: number;
  initialSection?: number;
  initialCompletedSections?: boolean[];
}

export function useAccordion({ totalSections, initialSection = 0, initialCompletedSections }: UseAccordionOptions) {
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [completedSections, setCompletedSections] = useState<boolean[]>(
    initialCompletedSections ?? Array(totalSections).fill(false)
  );

  const canOpen = useCallback(
    (index: number) => {
      // 첫 번째 섹션은 항상 열 수 있음
      if (index === 0) return true;
      // 이전 섹션이 완료되어야 열 수 있음
      return completedSections[index - 1];
    },
    [completedSections]
  );

  const openSection = useCallback(
    (index: number) => {
      if (canOpen(index)) {
        setCurrentSection(index);
      }
    },
    [canOpen]
  );

  const completeSection = useCallback(
    (index: number) => {
      setCompletedSections((prev) => {
        const next = [...prev];
        next[index] = true;
        return next;
      });
      // 자동으로 다음 섹션 열기
      if (index < totalSections - 1) {
        setCurrentSection(index + 1);
      }
    },
    [totalSections]
  );

  const resetSection = useCallback((index: number) => {
    setCompletedSections((prev) => {
      const next = [...prev];
      // 해당 섹션 이후 모두 미완료 처리
      for (let i = index; i < next.length; i++) {
        next[i] = false;
      }
      return next;
    });
    setCurrentSection(index);
  }, []);

  const invalidateSection = useCallback((index: number) => {
    setCompletedSections((prev) => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
  }, []);

  const closeAll = useCallback(() => {
    setCurrentSection(-1);
  }, []);

  return {
    currentSection,
    completedSections,
    openSection,
    completeSection,
    resetSection,
    invalidateSection,
    closeAll,
    canOpen,
  };
}
